import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company logo URL
const LOGO_URL = "/challenge-pack-logo.png";

const LabelPreview = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/inventory`);
        const found = response.data.find(i => i.reel_id === id);
        if (found) {
          // If item has a Google Drive link, redirect to it
          if (found.drive_link) {
            window.location.href = found.drive_link;
            return;
          }
          setItem(found);
        } else {
          setError("Item not found");
        }
      } catch (err) {
        console.error("Error fetching item:", err);
        setError("Failed to load item data");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItem();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-6 text-center">
          <h1 className="text-red-400 text-2xl font-bold mb-2">Error</h1>
          <p className="text-white">{error || "Item not found"}</p>
          <p className="text-gray-400 text-sm mt-2">Stock ID: {id}</p>
        </div>
      </div>
    );
  }

  const isBitReel = item.is_bit_reel === true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Label Card */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-4 flex items-center gap-4">
            <img 
              src={LOGO_URL} 
              alt="CPI Logo" 
              className="w-16 h-16 object-contain bg-white rounded-lg p-1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="flex-1 text-white">
              <h1 className="text-xl font-bold">
                {isBitReel ? "BIT REEL LABEL" : "RM LABEL"}
              </h1>
              <p className="text-teal-100 text-sm">Challenge Pack Industries</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Stock ID - Highlighted */}
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg p-4 mb-4 text-center">
              <p className="text-cyan-100 text-xs uppercase tracking-wide">Stock ID</p>
              <p className="text-white text-2xl font-bold font-mono">{item.reel_id}</p>
            </div>

            {/* Details Grid */}
            <div className="space-y-3">
              {isBitReel && item.parent_reel_id && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Parent Stock</span>
                  <span className="text-gray-800 font-semibold">{item.parent_reel_id}</span>
                </div>
              )}

              {item.reel_size && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Reel Size</span>
                  <span className="text-gray-800 font-semibold">{item.reel_size}"</span>
                </div>
              )}

              {item.mill_reel_no && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Mill Reel No</span>
                  <span className="text-gray-800 font-semibold">{item.mill_reel_no}</span>
                </div>
              )}

              {item.rm_name && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">RM Name</span>
                  <span className="text-gray-800 font-semibold">{item.rm_name}</span>
                </div>
              )}

              {/* Weight - Highlighted */}
              <div className="bg-amber-50 rounded-lg p-3 flex justify-between items-center">
                <span className="text-amber-700 font-medium">Weight</span>
                <span className="text-amber-800 text-xl font-bold">{item.reel_weight || "-"} Kg</span>
              </div>

              {item.product && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Product</span>
                  <span className="text-gray-800 font-semibold">{item.product}</span>
                </div>
              )}

              {item.gsm && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">GSM</span>
                  <span className="text-gray-800 font-semibold">{item.gsm}</span>
                </div>
              )}

              {item.bf && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">BF</span>
                  <span className="text-gray-800 font-semibold">{item.bf}</span>
                </div>
              )}

              {item.shade && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Shade</span>
                  <span className="text-gray-800 font-semibold">{item.shade}</span>
                </div>
              )}

              {(item.mill_name || item.dealer_name) && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Mill / Dealer</span>
                  <span className="text-gray-800 font-semibold text-right text-sm">
                    {item.mill_name}{item.mill_name && item.dealer_name && " - "}{item.dealer_name}
                  </span>
                </div>
              )}

              {item.status && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-500 font-medium">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    item.status === 'available' ? 'bg-green-100 text-green-700' :
                    item.status === 'stock_used' ? 'bg-gray-100 text-gray-700' :
                    item.status === 'return' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {item.status === 'available' ? 'Available' :
                     item.status === 'stock_used' ? 'Stock Used' :
                     item.status === 'return' ? 'Return' :
                     item.status}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 px-4 py-3 text-center">
            <p className="text-gray-500 text-xs">
              Scanned on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>

        {/* CPI Branding */}
        <div className="text-center mt-4">
          <p className="text-gray-500 text-sm">Challenge Pack Industries</p>
        </div>
      </div>
    </div>
  );
};

export default LabelPreview;
